import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard, Loader, KpiCard } from "@/components/common";
import { formatINR } from "@/lib/format";
import { Megaphone, TrendingUp, TrendingDown, Target } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";

export default function Marketing() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/marketing").then((r) => setData(r.data)); }, []);
  if (!data) return <Loader label="Loading marketing…" />;
  const s = data.summary;

  return (
    <div>
      <PageHeader title="Marketing Analytics" subtitle="What you spent, what it earned, and what's worth repeating." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="mkt-spend" index={0} label="Total Spend" value={formatINR(s.total_spend, { compact: true })} icon={Megaphone} accent="warning" />
        <KpiCard testid="mkt-sales" index={1} label="Attributed Sales" value={formatINR(s.attributed_sales, { compact: true })} icon={TrendingUp} accent="success" />
        <KpiCard testid="mkt-roi" index={2} label="Overall ROI" value={`${s.overall_roi}%`} icon={Target} accent={s.overall_roi >= 100 ? "success" : "danger"} />
        <KpiCard testid="mkt-campaigns" index={3} label="Campaigns" value={s.campaigns} icon={Megaphone} accent="primary" />
      </div>

      {(data.best || data.worst) && (
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {data.best && (
            <div className="rounded-xl border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 p-5 animate-fade-up" data-testid="mkt-best">
              <div className="flex items-center gap-2 text-[hsl(var(--success))] font-semibold"><TrendingUp className="h-4 w-4" /> Best Performer</div>
              <div className="font-heading text-lg font-bold mt-2">{data.best.name}</div>
              <p className="text-sm text-muted-foreground mt-1">{data.best.channel} · {data.best.roi}% ROI · earned {formatINR(data.best.attributed_sales, { compact: true })} on {formatINR(data.best.spend, { compact: true })}. Double down here.</p>
            </div>
          )}
          {data.worst && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5 animate-fade-up" data-testid="mkt-worst">
              <div className="flex items-center gap-2 text-destructive font-semibold"><TrendingDown className="h-4 w-4" /> Needs Review</div>
              <div className="font-heading text-lg font-bold mt-2">{data.worst.name}</div>
              <p className="text-sm text-muted-foreground mt-1">{data.worst.channel} · {data.worst.roi}% ROI. Consider pausing or reworking this campaign.</p>
            </div>
          )}
        </div>
      )}

      <SectionCard title="Campaign ROI" className="mb-6" testid="mkt-chart">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.campaigns} margin={{ left: -18, right: 6 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" opacity={0.5} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} interval={0} angle={-12} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
            <Tooltip formatter={(v) => `${v}% ROI`} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))" }} />
            <Bar dataKey="roi" radius={[4, 4, 0, 0]}>
              {data.campaigns.map((c, i) => <Cell key={i} fill={c.roi >= 200 ? "#10B981" : c.roi >= 100 ? "#6366F1" : "#EF4444"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </SectionCard>

      <SectionCard title="All Campaigns" testid="mkt-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="text-left font-semibold py-3">Campaign</th>
              <th className="text-left font-semibold py-3 hidden sm:table-cell">Channel</th>
              <th className="text-right font-semibold py-3">Spend</th>
              <th className="text-right font-semibold py-3">Sales</th>
              <th className="text-right font-semibold py-3">ROI</th>
            </tr></thead>
            <tbody>
              {data.campaigns.map((c) => (
                <tr key={c.id} className="border-b border-border/60 hover:bg-secondary/40 transition-colors">
                  <td className="py-3 font-medium">{c.name}</td>
                  <td className="py-3 hidden sm:table-cell text-muted-foreground">{c.channel}</td>
                  <td className="py-3 text-right tabular-nums">{formatINR(c.spend, { compact: true })}</td>
                  <td className="py-3 text-right tabular-nums">{formatINR(c.attributed_sales, { compact: true })}</td>
                  <td className={`py-3 text-right font-semibold tabular-nums ${c.roi >= 100 ? "text-[hsl(var(--success))]" : "text-destructive"}`}>{c.roi}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
